"use client";

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Key used for storing the about section state in localStorage
const STORAGE_KEY = "about-section-open";

type AboutContextType = {
  isOpen: boolean;
  toggle: () => void;
};

const AboutContext = createContext<AboutContextType | undefined>(undefined);

export function AboutProvider({ children }: { children: ReactNode }) {
  // Initialize with default state (false)
  const [isOpen, setIsOpen] = useState(false);

  // Load saved state from localStorage on component mount
  useEffect(() => {
    // Check if window is defined (client-side)
    if (typeof window !== "undefined") {
      const savedState = localStorage.getItem(STORAGE_KEY);
      // Only update state if there's a saved value
      if (savedState !== null) {
        setIsOpen(savedState === "true");
      }
    }
  }, []);

  // Toggle function that updates both state and localStorage
  const toggle = () => {
    setIsOpen(prev => {
      const newState = !prev;
      // Update localStorage with the new state
      if (typeof window !== "undefined") {
        localStorage.setItem(STORAGE_KEY, String(newState));
      }
      return newState;
    });
  };

  return (
    <AboutContext.Provider value={{ isOpen, toggle }}>
      {children}
    </AboutContext.Provider>
  );
}

export function useAbout() {
  const context = useContext(AboutContext);
  if (!context) {
    throw new Error("useAbout must be used within a AboutProvider");
  }
  return context;
}